import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../_shared/hooks';
import {
  fetchEnrichmentFlags,
  createEnrichmentFlag,
  updateEnrichmentFlag,
  deleteEnrichmentFlag,
  reorderEnrichmentFlags,
} from '../api-client';
import type { EnrichmentFlag } from '../components/types';

const QUERY_KEY = ['launcher', 'enrichment-flags'] as const;

export function useEnrichmentFlags() {
  const { apiFetch } = useApi();
  const query = useQuery<EnrichmentFlag[]>({
    queryKey: QUERY_KEY,
    queryFn: () => fetchEnrichmentFlags(apiFetch),
    staleTime: 0,
  });
  return {
    flags: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  } as const;
}

export function useCreateEnrichmentFlag() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Omit<EnrichmentFlag, 'id'>) => createEnrichmentFlag(apiFetch, payload),
    onSuccess: (created) => {
      queryClient.setQueryData(QUERY_KEY, (prev: EnrichmentFlag[] | undefined) =>
        prev ? [...prev, created] : [created],
      );
    },
  });
}

export function useUpdateEnrichmentFlag() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<EnrichmentFlag> & { id: string }) =>
      updateEnrichmentFlag(apiFetch, id, payload),
    onMutate: async ({ id, ...payload }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const prev = queryClient.getQueryData<EnrichmentFlag[]>(QUERY_KEY);
      if (prev) {
        queryClient.setQueryData(QUERY_KEY, prev.map(f => f.id === id ? { ...f, ...payload } : f));
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(QUERY_KEY, ctx.prev);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(QUERY_KEY, (prev: EnrichmentFlag[] | undefined) =>
        prev ? prev.map(f => f.id === updated.id ? updated : f) : prev,
      );
    },
  });
}

export function useDeleteEnrichmentFlag() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEnrichmentFlag(apiFetch, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const prev = queryClient.getQueryData<EnrichmentFlag[]>(QUERY_KEY);
      if (prev) {
        queryClient.setQueryData(QUERY_KEY, prev.filter(f => f.id !== id));
      }
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(QUERY_KEY, ctx.prev);
    },
  });
}

export function useReorderEnrichmentFlags() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => reorderEnrichmentFlags(apiFetch, ids),
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const prev = queryClient.getQueryData<EnrichmentFlag[]>(QUERY_KEY);
      if (prev) {
        const ordered = ids.map((id, i) => {
          const f = prev.find(x => x.id === id)!;
          return { ...f, order: i };
        });
        queryClient.setQueryData(QUERY_KEY, ordered);
      }
      return { prev };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(QUERY_KEY, ctx.prev);
    },
  });
}
